import { useMemo } from 'react';
import { Plus, Truck } from 'lucide-react';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../layout/ListFiltr';

const SECTION_COLOR = '#3B82F6';

// Barvy štítků — stabilní podle kódu kategorie, ne podle pořadí v seznamu
// (jinak by přebarvení jedné kategorie přemalovalo i ostatní).
const STITEK_BARVY = {
    suroviny:   '#10B981',
    spotrebka:  '#EAB308',
    obaly:      '#06B6D4',
    palivo:     '#F97316',
    sluzby:     '#8B5CF6',
    it:         '#3B82F6',
    marketing:  '#EC4899',
    pojistovna: '#14B8A6',
    energie:    '#F59E0B',
    jine:       '#94A3B8',
};
const barvaStitku = (kod) => STITEK_BARVY[kod] || '#94A3B8';

export const ZPUSOB_OBJEDNAVKY_LABEL = {
    eshop: 'E-shop', telefon: 'Telefon', email: 'E-mail',
    sms: 'SMS', whatsapp: 'WhatsApp', osobne: 'Osobně',
};

/** Jak se dodavateli platí — stejná sada jako na dokladu. */
export const ZPUSOB_PLATBY_LABEL = {
    prevod: 'Převod', hotovost: 'Hotovost', karta: 'Karta', inkaso: 'Inkaso',
};

/**
 * Kudy se od dodavatele dostane faktura do systému.
 *
 * `popis` není dekorace: „sken" a „podatelna" jsou jinak dvě slova bez
 * rozdílu a nikdo nepozná, co má u koho vybrat.
 */
export const PRICHOD_FAKTURY = {
    sken:      { label: 'Sken na místě',     popis: 'účtenku vyfotí obsluha telefonem' },
    podatelna: { label: 'Podatelna lami@',   popis: 'dodavatel posílá PDF, podatelna ho sama založí' },
    eshop:     { label: 'Stahuje se z e-shopu', popis: 'fakturu si stáhneme z portálu dodavatele' },
    papir:     { label: 'Papír — ručně',     popis: 'nechodí nic elektronicky' },
};

const DNY_ZKRATKY = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

/**
 * Dny z kalendáře jako „Po, Čt" — prázdné vrací null, ne pomlčku.
 *
 * Celý týden se píše slovem: „Po, Út, St, Čt, Pá, So, Ne" je sedm zkratek,
 * které v mřížce zalomí řádek na dva, a přitom neříká nic než „denně".
 */
const dnySlovy = (dny, klic) => {
    const vybrane = (dny || []).filter(d => d[klic]).map(d => DNY_ZKRATKY[d.den]);
    if (vybrane.length === 7) return 'denně';
    return vybrane.length ? vybrane.join(', ') : null;
};

const fmtKc = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('cs-CZ'));

function ListSkeleton({ isDark }) {
    const bar = (w, h = 9) => (
        <div style={{ width: w, height: h, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }} />
    );
    return (
        <div className="animate-pulse">
            {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center gap-2.5" style={{ padding: '10px 12px' }}>
                    <div className="flex-1 space-y-1.5">
                        {bar(`${55 + (i * 7) % 35}%`, 10)}
                        {bar('40%')}
                    </div>
                </div>
            ))}
        </div>
    );
}

// Štítek kategorie — barevná tečka + název, ať je vidět i v hustém seznamu
export function Stitek({ kod, nazev, size = 'sm' }) {
    const c = barvaStitku(kod);
    return (
        <span className={`inline-flex items-center gap-1 rounded-full font-medium ${size === 'sm' ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[11px]'}`}
            style={{ background: `${c}1f`, color: c, border: `1px solid ${c}33` }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: c, flexShrink: 0 }} />
            {nazev}
        </span>
    );
}

/**
 * Pohledy = předem dané sady dat. Zúžení sady (kategorie, platba) je filtr.
 *
 * Exportované, protože klávesová navigace šipkami v modulu musí jezdit po
 * TOMTÉŽ seznamu, který je vidět — jinak šipka skočí na dodavatele, který
 * není na obrazovce.
 */
export const POHLEDY = [
    { id: 'aktivni',   label: 'Aktivní',      test: d => d.aktivni },
    { id: 'mailem',    label: 'Chodí mailem', test: d => d.aktivni && d.zpusob_prichodu_faktury === 'podatelna' },
    { id: 'bez_ico',   label: 'Bez IČO',      test: d => d.aktivni && !d.ico && !d.je_zahranicni, upozorneni: true },
    // Od 3.482 sem spadly vratné obaly ze zrušené samostatné záložky. Pohled
    // odpovídá na otázku, kvůli které se tam chodilo: u koho nám leží peníze
    // v zálohách. Součet je pak vidět ve sloupci vedle.
    { id: 'obaly',     label: 'Drží naše obaly', test: d => (d.obaly_zaparkovano_kc || 0) > 0 },
    { id: 'neaktivni', label: 'Neaktivní',    test: d => !d.aktivni },
    { id: 'vse',       label: 'Vše',          test: () => true },
];

/**
 * Seznam dodavatelů — hlavička i chování podle normy rozvržení.
 *
 * Kategorie tu nejsou stromem (dodavatel jich má víc naráz, takže by se v něm
 * opakoval a hledání by trvalo dýl), ale **štítky** na řádku + filtr nahoře.
 * Řazení je vždycky podle abecedy — jediné, které dává u dodavatelů smysl.
 *
 * **Dvě hustoty, jedno rozmístění.** Bez vybraného dodavatele je seznam
 * přehled přes celou šířku a kreslí se jako **mřížka**: dodavatel, IČO,
 * objednávání (způsob, dny, lhůta), závoz, platba, kudy chodí faktura a obrat
 * za 12 měsíců. Teprve to z něj dělá odpověď na otázky, které dnes nemá kde
 * položit — komu platíme kartou, od koho čekat fakturu mailem, u koho se
 * objednává ve čtvrtek. Se zúženým sloupcem (vedle detailu) zbydou dva
 * řádky na položku, protože víc se do navigační šířky nevejde.
 */
export default function DodavateleListPanel({
    siroky = false, kompaktni = false, isDark, mutedColor, secondaryColor, inputStyle,
    search, setSearch,
    pohled, setPohled,
    filtrHodnoty, setFiltrHodnoty,
    kategorie = [], dodavatele = [], loading,
    activeId, onSelect, onNew, rezervaDole = null,
}) {
    const searchActive = !!search.trim();
    const aktivniPohled = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];

    const filtrSkupiny = useMemo(() => [
        {
            id: 'kategorie', typ: 'dropdown', label: 'Kategorie', labelKratky: 'Kategorie',
            options: kategorie.map(k => ({ value: k.kod, label: k.nazev })),
        },
        {
            id: 'platba', typ: 'dropdown', label: 'Způsob platby', labelKratky: 'Platba',
            options: Object.entries(ZPUSOB_PLATBY_LABEL).map(([value, label]) => ({ value, label })),
        },
        {
            id: 'faktura', typ: 'dropdown', label: 'Jak chodí faktura', labelKratky: 'Faktura',
            options: Object.entries(PRICHOD_FAKTURY).map(([value, m]) => ({ value, label: m.label })),
        },
    ], [kategorie]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return dodavatele
            .filter(d => {
                if (!aktivniPohled.test(d)) return false;
                if (filtrHodnoty.kategorie && !(d.kategorie || []).some(k => k.kod === filtrHodnoty.kategorie)) return false;
                if (filtrHodnoty.platba && d.zpusob_platby !== filtrHodnoty.platba) return false;
                if (filtrHodnoty.faktura && d.zpusob_prichodu_faktury !== filtrHodnoty.faktura) return false;
                if (!q) return true;
                return [d.nazev, d.ico, d.kontaktni_osoba, d.email].some(v => (v || '').toLowerCase().includes(q));
            })
            .sort((a, b) => (a.nazev || '').localeCompare(b.nazev || '', 'cs'));
    }, [dodavatele, search, aktivniPohled, filtrHodnoty]);

    const pocetPohledu = (p) => dodavatele.filter(p.test).length;
    const nastavFiltr = (id, hodnota) => setFiltrHodnoty(h => ({ ...h, [id]: hodnota || '' }));
    const resetFiltru = () => setFiltrHodnoty({ kategorie: '', platba: '', faktura: '' });

    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <ListToolbar
                siroky={siroky} kompaktni={kompaktni} isDark={isDark}
                mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{
                    value: search, onChange: setSearch,
                    placeholder: 'Hledat dodavatele, IČO, kontakt…', placeholderKratky: 'Hledat',
                }}
                akce={[{ id: 'novy', label: 'Nový dodavatel', Icon: Plus, onClick: onNew, primarni: true }]}
                filtr={(
                    <FiltrTlacitko
                        skupiny={filtrSkupiny} hodnoty={filtrHodnoty}
                        onChange={nastavFiltr} onReset={resetFiltru}
                        pocetVysledku={filtered.length}
                        siroky={siroky} kompaktni={kompaktni}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR}
                    />
                )}
                pohled={POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocetPohledu(p)}
                        aktivni={pohled === p.id} upozorneni={p.upozorneni && pocetPohledu(p) > 0}
                        onClick={() => setPohled(p.id)}
                        barva={SECTION_COLOR} isDark={isDark} mutedColor={mutedColor} kompaktni={kompaktni} />
                ))}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={filtrSkupiny} hodnoty={filtrHodnoty}
                        onChange={nastavFiltr} onReset={resetFiltru}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
            />

            {/* Záhlaví mřížky — jen v přehledu; v navigační šířce by se nevešlo. */}
            {siroky && !loading && filtered.length > 0 && (
                <div className="flex items-center gap-4" style={{
                    padding: '10px 14px', fontSize: 10.5, fontWeight: 700,
                    letterSpacing: '0.06em', color: mutedColor, flexShrink: 0,
                }}>
                    <span style={{ flex: '2.4 1 0', minWidth: 0 }}>DODAVATEL</span>
                    <span style={{ flex: '1 1 0' }}>IČO</span>
                    <span style={{ flex: '1.5 1 0' }}>OBJEDNÁVKA</span>
                    <span style={{ flex: '1 1 0' }}>ZÁVOZ</span>
                    <span style={{ flex: '1 1 0' }}>PLATBA</span>
                    <span style={{ flex: '1.4 1 0' }}>FAKTURA CHODÍ</span>
                    <span style={{ flex: '1.1 1 0', textAlign: 'right' }}>OBRAT 12 M</span>
                    <span style={{ flex: '1 1 0', textAlign: 'right' }}>ZÁLOHY V OBALECH</span>
                </div>
            )}

            <div className="overflow-y-auto" style={{
                flex: 1, minHeight: 0,
                borderTop: siroky ? `1px solid ${hranice}` : 'none',
                paddingBottom: rezervaDole || undefined,
            }}>
                {loading ? (
                    <ListSkeleton isDark={isDark} />
                ) : filtered.length === 0 ? (
                    searchActive || filtrHodnoty.kategorie || filtrHodnoty.platba || filtrHodnoty.faktura ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>
                            {searchActive ? `Nic nenalezeno pro „${search}"` : 'Filtru nikdo neodpovídá'}
                        </p>
                    ) : (
                        <div className="text-center px-6 py-10">
                            <Truck size={28} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 10px' }} />
                            <p className="text-xs" style={{ color: mutedColor }}>
                                {pohled === 'neaktivni' ? 'Žádní neaktivní dodavatelé' : 'Zatím žádní dodavatelé'}
                            </p>
                            {pohled !== 'neaktivni' && (
                                <button onClick={onNew} className="mt-3 mx-auto flex items-center gap-1.5"
                                    style={{ background: SECTION_COLOR, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                    <Plus size={13} /> Vytvořit prvního
                                </button>
                            )}
                        </div>
                    )
                ) : filtered.map(d => (
                    siroky
                        ? <RadekMrizka key={d.id} d={d} aktivni={activeId === d.id} onSelect={onSelect}
                            isDark={isDark} mutedColor={mutedColor} hranice={hranice} />
                        : <RadekUzky key={d.id} d={d} aktivni={activeId === d.id} onSelect={onSelect}
                            isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} hranice={hranice} kompaktni={kompaktni} />
                ))}
            </div>

            {/* Počet dole — orientace, kolik jich filtrem prošlo */}
            {!loading && filtered.length > 0 && (
                <div className="px-3 py-2 text-[10px]" style={{ borderTop: `1px solid ${hranice}`, color: mutedColor, flexShrink: 0 }}>
                    {filtered.length} {filtered.length === 1 ? 'dodavatel' : filtered.length < 5 ? 'dodavatelé' : 'dodavatelů'}
                </div>
            )}
        </div>
    );
}

/** Přehled přes celou šířku — sloupcová mřížka (norma). */
function RadekMrizka({ d, aktivni, onSelect, isDark, mutedColor, hranice }) {
    const objednavka = [
        ZPUSOB_OBJEDNAVKY_LABEL[d.zpusob_objednavky],
        dnySlovy(d.dny, 'objednavka'),
    ].filter(Boolean).join(' · ');
    const zavoz = dnySlovy(d.dny, 'zavoz');
    const faktura = PRICHOD_FAKTURY[d.zpusob_prichodu_faktury]?.label;
    const tlumene = !d.aktivni;

    return (
        <button type="button" onClick={() => onSelect(d.id)} data-list-id={d.id}
            className="flex items-center gap-4 w-full text-left"
            style={{
                padding: '11px 14px', borderBottom: `1px solid ${hranice}`, border: 'none',
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? (isDark ? 'rgba(59,130,246,0.10)' : 'rgba(59,130,246,0.06)') : 'transparent',
                fontSize: 13, color: tlumene ? mutedColor : 'var(--text-primary)', cursor: 'pointer',
            }}>
            <span style={{ flex: '2.4 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: d.aktivni ? '#10B981' : 'rgba(148,163,184,0.5)',
                }} />
                <span className="truncate font-semibold">{d.nazev}</span>
                {(d.kategorie || []).slice(0, 2).map(k => <Stitek key={k.kod} kod={k.kod} nazev={k.nazev} />)}
            </span>
            <span style={{ flex: '1 1 0', fontVariantNumeric: 'tabular-nums', color: mutedColor }}>
                {d.ico || (d.je_zahranicni ? d.dic || 'zahraniční' : <span style={{ color: '#F87171' }}>chybí</span>)}
            </span>
            <span style={{ flex: '1.5 1 0', minWidth: 0 }} className="truncate">
                {objednavka || <span style={{ color: mutedColor }}>—</span>}
                {d.dodaci_lhuta_dni != null && (
                    <span style={{ color: mutedColor }}> · {d.dodaci_lhuta_dni} d</span>
                )}
            </span>
            <span style={{ flex: '1 1 0', minWidth: 0, color: zavoz ? undefined : mutedColor }} className="truncate">{zavoz || '—'}</span>
            <span style={{ flex: '1 1 0', color: d.zpusob_platby ? undefined : mutedColor }}>
                {ZPUSOB_PLATBY_LABEL[d.zpusob_platby] || '—'}
            </span>
            <span style={{ flex: '1.4 1 0', minWidth: 0 }} className="truncate">
                {faktura
                    ? <span style={{ color: d.zpusob_prichodu_faktury === 'podatelna' ? SECTION_COLOR : undefined }}>{faktura}</span>
                    : <span style={{ color: mutedColor }}>—</span>}
            </span>
            <span style={{ flex: '1.1 1 0', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {fmtKc(d.obrat_12m)}
            </span>
            {/* Zálohy za vratné obaly — od 3.482 sloupec dodavatele místo vlastní
                záložky. Je to pohledávka vůči němu, takže patří k obratu a
                splatnosti, ne do samostatné agendy. */}
            <span style={{ flex: '1 1 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                title={d.obaly_kusu ? `držíme ${d.obaly_kusu} ks` : undefined}>
                {d.obaly_zaparkovano_kc
                    ? fmtKc(d.obaly_zaparkovano_kc)
                    : <span style={{ color: mutedColor }}>—</span>}
            </span>
        </button>
    );
}

/** Navigační šířka vedle detailu — dva řádky na položku. */
function RadekUzky({ d, aktivni, onSelect, isDark, mutedColor, secondaryColor, hranice, kompaktni }) {
    return (
        <button type="button" onClick={() => onSelect(d.id)} data-list-id={d.id}
            className="w-full text-left"
            style={{
                padding: kompaktni ? '11px 12px' : '9px 10px', border: 'none',
                borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? (isDark ? 'rgba(59,130,246,0.10)' : 'rgba(59,130,246,0.06)') : 'transparent',
                cursor: 'pointer',
            }}>
            <div className="flex items-center gap-2 min-w-0">
                <span style={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: d.aktivni ? '#10B981' : 'rgba(148,163,184,0.5)',
                }} />
                <span className="truncate font-semibold" style={{ fontSize: kompaktni ? 14 : 12.5, color: aktivni ? SECTION_COLOR : 'var(--text-primary)' }}>
                    {d.nazev}
                </span>
                {d.obrat_12m > 0 && (
                    <span className="ml-auto shrink-0 tabular-nums" style={{ fontSize: 11, color: mutedColor }}>{fmtKc(d.obrat_12m)}</span>
                )}
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {(d.kategorie || []).map(k => <Stitek key={k.kod} kod={k.kod} nazev={k.nazev} />)}
                {d.ico
                    ? <span className="text-[9.5px] font-mono" style={{ color: mutedColor }}>IČO {d.ico}</span>
                    : <span className="text-[9.5px]" style={{ color: 'rgba(239,68,68,0.8)' }}>bez IČO</span>}
                {d.zpusob_objednavky && (
                    <span className="text-[9.5px]" style={{ color: secondaryColor }}>
                        {ZPUSOB_OBJEDNAVKY_LABEL[d.zpusob_objednavky]}
                    </span>
                )}
                {d.zpusob_prichodu_faktury === 'podatelna' && (
                    <span className="text-[9.5px]" style={{ color: SECTION_COLOR }}>mailem</span>
                )}
            </div>
        </button>
    );
}
